import React, { useEffect, useState } from 'react'
import useIdStabile from './useIdStabile'
import styled from 'styled-components'
import ChevronDown from '../../images/ico_chevron_down.svg'
import { useController, Control } from 'react-hook-form'

interface SelectProps {
  name: string
  options: Options[]
  /** Testo della label VISIBILE associata al controllo (WCAG 3.3.2). */
  placeHolderText: string
  icon?: string
  dataFromSelect?: (data: ISelectData) => any
  isTextBold?: boolean
  required?: boolean
  selectContainerBackground?: string
  control: Control<any>
  rules?: any
}

export interface Options {
  value: string
  label: string
}

export interface ISelectData {
  name: string
  value: string
  category: string
}

const CHIAVE_STORAGE = 'selectData'

function leggiDaStorage(categoria: string): ISelectData | undefined {
  try {
    const dati = JSON.parse(localStorage.getItem(CHIAVE_STORAGE) || '[]')
    return Array.isArray(dati) ? dati.find((o: ISelectData) => o.category === categoria) : undefined
  } catch {
    return undefined
  }
}

function scriviSuStorage(dato: ISelectData) {
  try {
    const dati = JSON.parse(localStorage.getItem(CHIAVE_STORAGE) || '[]')
    const lista: ISelectData[] = Array.isArray(dati) ? dati : []
    const i = lista.findIndex((e) => e.category === dato.category)
    if (i > -1) lista[i] = dato
    else lista.push(dato)
    localStorage.setItem(CHIAVE_STORAGE, JSON.stringify(lista))
  } catch {
    /* storage non disponibile: la selezione resta comunque nel form */
  }
}

/**
 * Select basata su <select> nativo (decisione D1 in documentazione della remediation a11y, fuori dal repo).
 *
 * Il widget nativo porta gratis ruolo combobox, focus, Enter/Space, frecce,
 * Home/End, type-ahead ed Escape — tutto cio' che la versione custom
 * (<div tabIndex={0} role="button">) doveva reimplementare e sbagliava.
 * Chiude AQLN0, 3YJXV, Q9L3F, ABB9J, TX4UG.
 */
const Select: React.FC<SelectProps> = ({
  name,
  options,
  placeHolderText,
  icon,
  dataFromSelect,
  isTextBold = false,
  required = false,
  selectContainerBackground,
  control,
  rules,
}) => {
  const idUnico = useIdStabile()
  const selectId = `select-${name}-${idUnico}`
  const errorId = `${selectId}-errore`

  const { field, fieldState } = useController({ name, control, rules })
  const [valore, setValore] = useState<string>(() => leggiDaStorage(name)?.value ?? '')

  // Primo valore al chiamante anche senza interazione, come faceva la versione precedente.
  useEffect(() => {
    const iniziale = leggiDaStorage(name)
    dataFromSelect?.(iniziale || { name: '', value: '', category: name })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const nuovoValore = e.target.value
    const opzione = options.find((o) => o.value === nuovoValore)
    const dato: ISelectData = {
      name: opzione?.label ?? '',
      value: nuovoValore,
      category: name,
    }

    setValore(nuovoValore)
    field.onChange(nuovoValore)
    dataFromSelect?.(dato)
    scriviSuStorage(dato)
  }

  return (
    <Campo>
      <Label htmlFor={selectId}>{placeHolderText}</Label>

      <Contenitore $background={selectContainerBackground}>
        {icon && <Icona style={{ backgroundImage: `url(${icon})` }} aria-hidden="true" />}

        <NativeSelectEl
          id={selectId}
          ref={field.ref}
          name={field.name}
          value={valore}
          onChange={handleChange}
          onBlur={field.onBlur}
          required={required}
          aria-required={required}
          aria-invalid={fieldState.error ? true : undefined}
          aria-describedby={fieldState.error ? errorId : undefined}
          $bold={isTextBold}
          $hasIcon={!!icon}
        >
          <option value="">{placeHolderText}</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </NativeSelectEl>
      </Contenitore>

      {/* role="alert": l'errore compare senza spostare il focus (WCAG 4.1.3). */}
      {fieldState.error && (
        <ErrorText id={errorId} role="alert">
          {fieldState.error.message}
        </ErrorText>
      )}
    </Campo>
  )
}

export default Select

// region Style
const Campo = styled.div`
  width: 100%;
  margin: 0 5px;
`

/* Label clippata, non visibile: il design non prevede un'etichetta sopra il
   controllo e il nome del campo si legge dentro la pillola, come prima della
   remediation. Resta nell'albero accessibile e associata via htmlFor, quindi
   lo screen reader annuncia "Chi sono" e non "macroprofessione" (Regola 2).
   Stessa tecnica di commons/footer/SearchBar. */
const Label = styled.label`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`

const Contenitore = styled.div<{ $background?: string }>`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  /* min-height, non height: il testo deve poter crescere (WCAG 1.4.12) */
  min-height: 50px;
  background-color: ${({ $background }) => $background || '#fff'};
  /* Nessun bordo e pillola a 30px: identico a main. Il bordo 1px #767676 che
     era stato aggiunto per 1.4.11 cambiava la resa, quindi e' stato rimosso:
     TX4UG e 6JPDK restano aperti e vanno chiusi con una scelta di design. */
  border-radius: 30px;
`

/* 7.3rem e non 8rem: su main il chevron era un terzo box flex da 60px che
   partecipava alla compressione, qui e' il background della select, quindi
   l'icona si comprimeva meno e spingeva il testo 5.4px a destra. Base 71.5px:
   ricavata misurando la frazione di compressione in ogni contenitore e
   risolvendo per la larghezza di main (52.3px a 270, 55.5 a 324.3, 56.2 a 338,
   57.3 a 360). Residuo dopo la correzione: 0.3px.
   Nessun background-size, altrimenti l'SVG viene scalato all'altezza del
   contenitore e risulta piu' grande. */
const Icona = styled.div`
  width: 7.15rem;
  align-self: stretch;
  background-position: center;
  background-repeat: no-repeat;
  margin-left: 2px;
`

const NativeSelectEl = styled.select<{ $bold: boolean; $hasIcon: boolean }>`
  flex: 1 1 auto;
  width: 100%;
  min-height: 48px;
  padding: 0.8rem 6rem 0.8rem ${({ $hasIcon }) => ($hasIcon ? '0' : '1.5rem')};
  border: 0;
  border-radius: 4px;
  background-color: transparent;
  background-image: url(${ChevronDown});
  background-repeat: no-repeat;
  /* Su main il chevron stava al centro di un box a filo del bordo destro, largo
     39-43px con l'icona (centro a 19.6-21.5px dal bordo) e 47px senza icona
     (centro a 23.5px). Con l'SVG largo 16px, "right 1.25rem" mette il centro a
     20.5px e "right 1.55rem" a 23.5px: entro 1px in tutti i casi misurati. */
  background-position: right ${({ $hasIcon }) => ($hasIcon ? '1.25rem' : '1.55rem')} center;
  /* #222 su bianco = 15.9:1 */
  color: #222222;
  font-family: TradeGothicLTPro, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif,
    'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol';
  font-size: 1.3rem;
  font-weight: ${({ $bold }) => ($bold ? 700 : 400)};
  /* -0.5px come nel design originale (era SelectedValue su main) */
  letter-spacing: -0.5px;
  cursor: pointer;
  appearance: none;

  &:focus {
    outline: none;
  }
  &:focus-visible {
    outline: 3px solid #005fcc;
    outline-offset: 2px;
  }
`

const ErrorText = styled.span`
  display: block;
  margin-top: 0.5rem;
  /* #b3261e su bianco = 5.3:1 */
  color: #b3261e;
  font-size: 1.1rem;
  font-weight: 700;
`
// endregion
